import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';

import css from './CartOrderSummary.module.css';

const { Money } = sdkTypes;

/**
 * CL-CART-01 — one seller's subtotal, shipping and payable figure (requirement 7).
 *
 * PER SELLER, not per cart. Section M puts "mixed-seller checkout in one payment" out of
 * scope: one payment covers one seller, so a cart-wide total is a number nobody can ever
 * pay. Rendering this inside each seller's panel is also what makes multi-vendor a UI
 * change rather than a rewrite, which is the whole point of grouping by seller.
 *
 * No discount row: D-15 defines the discount as the negotiated reduction on an offer, so a
 * Buy It Now basket can never have one. A permanent "$0.00" row teaches buyers to ignore
 * the row, which defeats showing the saving when it is real.
 *
 * Shipping is deliberately not totalled here. It depends on the delivery address (the zone
 * decides the price), it is combined across the basket rather than summed per row, and the
 * figure is computed by our server — never in the browser. Until an address exists the
 * honest answer is "calculated at checkout" (AC-18).
 *
 * Which is why the bottom line is labelled "subtotal before shipping" rather than "Total".
 * A "Total" identical to the item subtotal reads as a broken sum, not as a figure still
 * missing its shipping.
 */
const CartOrderSummary = props => {
  const {
    className,
    rootClassName,
    itemSubtotalInSubunits,
    currency,
    unavailableItemCount,
  } = props;
  const intl = useIntl();

  const money = amount =>
    Number.isInteger(amount) && currency ? formatMoney(intl, new Money(amount, currency)) : null;

  return (
    <div className={classNames(rootClassName || css.root, className)}>
      <p className={css.line}>
        <FormattedMessage id="CartOrderSummary.itemSubtotal" />
        <span className={css.amount}>{money(itemSubtotalInSubunits)}</span>
      </p>

      <p className={css.line}>
        <FormattedMessage id="CartOrderSummary.shipping" />
        <span className={css.pending}>
          <FormattedMessage id="CartOrderSummary.shippingAtCheckout" />
        </span>
      </p>

      {unavailableItemCount > 0 ? (
        <p className={css.excluded}>
          <FormattedMessage
            id="CartOrderSummary.excludedFromTotal"
            values={{ count: unavailableItemCount }}
          />
        </p>
      ) : null}

      {/* The one figure that has to be read at a glance, so it is the only large one. */}
      <div className={css.payable}>
        <p className={css.payableLabel}>
          <FormattedMessage id="CartOrderSummary.beforeShipping" />
        </p>
        <p className={css.payableAmount}>{money(itemSubtotalInSubunits)}</p>
      </div>
    </div>
  );
};

export default CartOrderSummary;
